import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useNavigate } from "react-router-dom";
import {
  FaPencilAlt,
  FaTrashAlt,
  FaStar,
  FaRegStar,
  FaSearch,
  FaPrint,
  FaCode,
} from "react-icons/fa";
import { useSelector } from "react-redux";

import { useDebounce } from "../hooks/useDebounced";
import {
  useSetDefaultLabelTemplateMutation,
  useGetAllLabelTemplatesQuery,
  useDeleteLabelTemplateMutation,
} from "../toolkitServices/labelTemplateApi";

import { useGetAllBranchesQuery } from "../toolkitServices/branchApi";
import { useGetAllCategoriesQuery } from "../toolkitServices/categoryApi";
import ConfirmDialog from "../components/ConfirmDialog";

const PrnLabelTemplateListPage = () => {
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 800);

  const authUser = useSelector((s) => s.auth?.user);
  const clientCode = authUser?.clientCode;

  const {
    data: templatesRes,
    isLoading,
    isError,
    error,
  } = useGetAllLabelTemplatesQuery(
    clientCode ? { clientCode, templateType: "prn" } : { templateType: "prn" },
    { skip: false },
  );

  const [deleteTemplate, { isLoading: isDeleting }] =
    useDeleteLabelTemplateMutation();
  const [setDefaultTemplate] = useSetDefaultLabelTemplateMutation();

  const { data: branchesRes } = useGetAllBranchesQuery(
    { page: 1, limit: 1000, active: true },
    { skip: false },
  );

  const { data: categoriesRes } = useGetAllCategoriesQuery(
    { page: 1, limit: 1000, active: true },
    { skip: false },
  );

  const allTemplates = useMemo(() => {
    const list = templatesRes?.data || templatesRes?.items || [];
    return Array.isArray(list) ? list : [];
  }, [templatesRes]);

  // ✅ Sirf PRN templates
  const templates = useMemo(() => {
    return allTemplates.filter((tpl) => tpl?.templateType === "prn");
  }, [allTemplates]);

  const branches = useMemo(() => {
    const list = branchesRes?.data || branchesRes?.items || [];
    return Array.isArray(list) ? list : [];
  }, [branchesRes]);

  const categories = useMemo(() => {
    const list = categoriesRes?.data || categoriesRes?.items || [];
    return Array.isArray(list) ? list : [];
  }, [categoriesRes]);

  const branchMap = useMemo(() => {
    const map = {};
    branches.forEach((b) => {
      map[b._id] = b.name || b.branchName || b.code || b.shortName || "Branch";
    });
    return map;
  }, [branches]);

  const categoryMap = useMemo(() => {
    const map = {};
    categories.forEach((c) => {
      map[c._id] = c.name || c.categoryName || c.code || "Category";
    });
    return map;
  }, [categories]);

  const filteredTemplates = useMemo(() => {
    if (!debouncedSearch) return templates;
    const term = debouncedSearch.toLowerCase();

    return templates.filter((tpl) => {
      const name = (tpl.name || "").toLowerCase();
      const size = `${tpl.widthMm || ""}x${tpl.heightMm || ""}`.toLowerCase();
      const prnCode = (tpl.prnCode || "").toLowerCase();

      return (
        name.includes(term) || size.includes(term) || prnCode.includes(term)
      );
    });
  }, [templates, debouncedSearch]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "l" && !templates?.length) {
        e.preventDefault();
        navigate("/designs/prn-label-templates/new");
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [navigate, templates]);

  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    tpl: null,
  });

  const openDeleteDialog = (tpl) => {
    setDeleteDialog({ open: true, tpl });
  };

  const closeDeleteDialog = () => {
    if (isDeleting) return;
    setDeleteDialog({ open: false, tpl: null });
  };

  const handleConfirmDelete = async () => {
    const tpl = deleteDialog.tpl;
    if (!tpl) return;

    try {
      await deleteTemplate(tpl._id).unwrap();
    } catch (err) {
      console.error("Delete PRN template error:", err);
    } finally {
      setDeleteDialog({ open: false, tpl: null });
    }
  };

  const handleSetDefault = async (tpl) => {
    try {
      await setDefaultTemplate({ id: tpl._id, clientCode }).unwrap();
    } catch (err) {
      console.error("Set default PRN template error:", err);
    }
  };

  const renderScopeChips = (names = [], allLabel) => {
    if (allLabel) {
      return (
        <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] text-emerald-700">
          {allLabel}
        </span>
      );
    }

    if (!names.length) {
      return (
        <span className="inline-flex items-center rounded-full border border-dashed border-slate-200 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-400">
          Not mapped
        </span>
      );
    }

    const shown = names.slice(0, 2);
    const remaining = names.length - shown.length;

    return (
      <div className="flex flex-wrap gap-1">
        {shown.map((n, idx) => (
          <span
            key={idx}
            className="inline-flex items-center rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-[10px] text-slate-700"
          >
            {n}
          </span>
        ))}
        {remaining > 0 && (
          <span
            className="inline-flex items-center rounded-full border border-dashed border-slate-300 bg-slate-50 px-2 py-0.5 text-[10px] text-slate-500"
            title={names.join(", ")}
          >
            +{remaining} more
          </span>
        )}
      </div>
    );
  };

  const renderScope = (tpl) => {
    let branchNames = [];
    let branchAllLabel = null;

    if (tpl.appliesToAllBranches) {
      branchAllLabel = "All branches";
    } else if (Array.isArray(tpl.branchIds) && tpl.branchIds.length) {
      branchNames = tpl.branchIds.map((id) => branchMap[id] || id);
    }

    let categoryNames = [];
    let categoryAllLabel = null;

    if (tpl.appliesToAllCategories) {
      categoryAllLabel = "All categories";
    } else if (Array.isArray(tpl.categoryIds) && tpl.categoryIds.length) {
      categoryNames = tpl.categoryIds.map((id) => categoryMap[id] || id);
    }

    return (
      <div className="flex flex-col gap-1 text-[11px] text-slate-700">
        <div className="flex items-start gap-1.5">
          <span className="mt-0.5 inline-flex h-5 rounded-full border border-violet-100 bg-violet-50 px-1.5 text-[9px] font-semibold uppercase tracking-wide text-violet-700">
            Branch
          </span>
          {renderScopeChips(branchNames, branchAllLabel)}
        </div>

        <div className="flex items-start gap-1.5">
          <span className="mt-0.5 inline-flex h-5 rounded-full border border-sky-100 bg-sky-50 px-1.5 text-[9px] font-semibold uppercase tracking-wide text-sky-700">
            Category
          </span>
          {renderScopeChips(categoryNames, categoryAllLabel)}
        </div>
      </div>
    );
  };

  return (
    <>
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between bg-[#9481ce] px-4 py-3 text-white">
          <div>
            <h2 className="text-lg font-semibold tracking-tight">
              PRN Label Templates
            </h2>
            <p className="mt-0.5 text-xs text-violet-100">
              Raw printer label templates – branch & category mapping ke saath.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="relative">
              <span className="absolute inset-y-0 left-2 flex items-center text-[11px] text-slate-400">
                <FaSearch />
              </span>
              <input
                type="text"
                placeholder="Search by name, size or PRN..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="rounded-md border border-violet-200/60 px-3 py-1.5 pl-7 text-sm text-gray-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-violet-200"
              />
            </div>

            <Link
              to="/designs/prn-label-templates/new"
              className="rounded-md border border-violet-100 bg-white px-4 py-1.5 text-sm font-medium text-[#5d4bb0] shadow-sm hover:bg-violet-50"
            >
              + Add New
            </Link>
          </div>
        </div>

        {/* Table */}
        <div className="max-h-[calc(100vh-170px)] overflow-x-auto overflow-y-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="sticky top-0 z-10 bg-[#f1eefc] text-xs uppercase text-[#4b3d91]">
              <tr>
                <th className="px-4 py-3 font-semibold">Template Name</th>
                <th className="px-4 py-3 font-semibold">Size (mm)</th>
                <th className="px-4 py-3 font-semibold">PRN Summary</th>
                <th className="px-4 py-3 font-semibold">Scope</th>
                <th className="px-4 py-3 text-center font-semibold">Default</th>
                <th className="px-4 py-3 font-semibold">Action</th>
              </tr>
            </thead>

            <tbody className="bg-white text-gray-700">
              <AnimatePresence>
                {isLoading ? (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <td colSpan="6" className="py-10">
                      <div className="flex flex-col items-center justify-center gap-3 text-slate-500">
                        <div className="h-8 w-8 animate-spin rounded-full border-2 border-violet-300 border-t-violet-600" />
                        <span className="text-sm">
                          Loading PRN templates...
                        </span>
                        <span className="text-[11px] text-slate-400">
                          Please wait, fetching mappings.
                        </span>
                      </div>
                    </td>
                  </motion.tr>
                ) : isError ? (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <td colSpan="6" className="py-6 text-center text-red-500">
                      Failed to load PRN label templates
                      <div className="mt-1 text-xs text-red-400">
                        {error?.data?.message || error?.error || ""}
                      </div>
                    </td>
                  </motion.tr>
                ) : filteredTemplates?.length ? (
                  filteredTemplates.map((tpl, index) => {
                    const prnCode = tpl?.prnCode || "";
                    const prnLines = prnCode
                      ? prnCode.split("\n").filter(Boolean).length
                      : 0;
                    const prnLength = prnCode.length;

                    return (
                      <motion.tr
                        key={tpl._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25, delay: index * 0.03 }}
                        className="border-t border-slate-100 transition-colors hover:bg-[#f6f4ff]"
                      >
                        {/* Name */}
                        <td className="align-top px-4 py-2">
                          <div className="flex flex-col gap-0.5">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-slate-800">
                                {tpl.name || "Untitled PRN Template"}
                              </span>

                              <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] text-indigo-700">
                                <FaPrint className="text-[9px]" />
                                PRN
                              </span>

                              {tpl.isDefault && (
                                <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] text-amber-700">
                                  <FaStar className="text-[9px]" />
                                  Default
                                </span>
                              )}
                            </div>

                            {tpl._id && (
                              <div className="mt-0.5 text-[10px] text-gray-400">
                                ID:{" "}
                                <span className="break-all font-mono">
                                  {tpl._id}
                                </span>
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Size */}
                        <td className="align-top px-4 py-2">
                          <div className="text-sm font-medium text-slate-800">
                            {tpl.widthMm || 0} × {tpl.heightMm || 0}
                          </div>
                          {tpl.dpi && (
                            <div className="text-[10px] text-gray-400">
                              {tpl.dpi} dpi
                            </div>
                          )}
                        </td>

                        {/* PRN Summary */}
                        <td className="align-top px-4 py-2">
                          <div className="flex flex-wrap items-center gap-2 text-[11px]">
                            <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-slate-700">
                              <FaCode className="text-[10px]" />
                              <span className="font-semibold">{prnLines}</span>
                              <span>Lines</span>
                            </span>

                            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-emerald-700">
                              <span className="font-semibold">{prnLength}</span>
                              <span>Chars</span>
                            </span>
                          </div>

                          {prnCode ? (
                            <div className="mt-2 max-w-[360px] truncate rounded-md border border-slate-200 bg-slate-50 px-2 py-1 font-mono text-[10px] text-slate-500">
                              {prnCode}
                            </div>
                          ) : (
                            <div className="mt-2 text-[10px] text-slate-400">
                              No PRN code
                            </div>
                          )}
                        </td>

                        {/* Scope */}
                        <td className="align-top px-4 py-2">
                          {renderScope(tpl)}
                        </td>

                        {/* Default */}
                        <td className="align-top px-4 py-2 text-center">
                          {tpl.isDefault ? (
                            <button
                              type="button"
                              className="inline-flex cursor-default items-center gap-1 rounded-full border border-amber-200 bg-amber-100 px-2 py-1 text-[11px] text-amber-700"
                              title="Current default template"
                            >
                              <FaStar className="text-[10px]" />
                              Default
                            </button>
                          ) : (
                            <button
                              type="button"
                              onClick={() => handleSetDefault(tpl)}
                              className="inline-flex items-center gap-1 rounded-full border border-slate-300 bg-slate-100 px-2 py-1 text-[11px] text-slate-700 hover:border-amber-200 hover:bg-amber-50 hover:text-amber-700"
                              title="Set as default template"
                            >
                              <FaRegStar className="text-[10px]" />
                              Set Default
                            </button>
                          )}
                        </td>

                        {/* Actions */}
                        <td className="align-top px-4 py-2">
                          <div className="flex items-center gap-1">
                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="rounded-full p-2 text-sm text-[#9481ce] hover:bg-[#9481ce]/15"
                              onClick={() =>
                                navigate(
                                  `/designs/prn-label-templates/edit/${tpl._id}`,
                                )
                              }
                              title="Edit"
                            >
                              <FaPencilAlt />
                            </motion.button>

                            <motion.button
                              whileHover={{ scale: 1.05 }}
                              whileTap={{ scale: 0.95 }}
                              className="rounded-full p-2 text-sm text-rose-500 hover:bg-rose-50"
                              title="Delete"
                              onClick={() => openDeleteDialog(tpl)}
                            >
                              <FaTrashAlt />
                            </motion.button>
                          </div>
                        </td>
                      </motion.tr>
                    );
                  })
                ) : (
                  <motion.tr initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                    <td colSpan="6" className="py-8 text-center text-gray-500">
                      <div className="flex flex-col items-center gap-1">
                        <span className="text-sm">
                          No matching PRN templates found.
                        </span>
                        {!templates?.length && (
                          <Link
                            to="/designs/prn-label-templates/new"
                            className="mt-1 inline-flex items-center gap-1 rounded-full bg-[#9481ce] px-3 py-1.5 text-xs text-white shadow-sm hover:bg-[#7f6bce]"
                          >
                            + Create your first PRN template
                          </Link>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                )}
              </AnimatePresence>
            </tbody>
          </table>
        </div>
      </div>

      <ConfirmDialog
        open={deleteDialog.open}
        onCancel={closeDeleteDialog}
        onConfirm={handleConfirmDelete}
        title="Delete PRN label template?"
        message={
          deleteDialog.tpl
            ? `Template "${deleteDialog.tpl.name || "Untitled"}" will be permanently deleted.`
            : "This template will be permanently deleted."
        }
        confirmText="Delete"
        cancelText="Cancel"
        loading={isDeleting}
        destructive
      />
    </>
  );
};

export default PrnLabelTemplateListPage;
